export type ThemeMode = "light" | "dark" | "system";

const THEME_STORAGE_KEY = "ls-theme";
const THEME_COOKIE_KEY = "ls-theme";

export function useLSTheme() {
	let mediaQueryListenerRegistered = false;
	/**
	 * Composable for controlling the application theme.
	 * Supports 'light', 'dark', and 'system' (follows OS preference) modes.
	 * Persists the user's preference to both cookie (for FOUC prevention) and localStorage.
	 */
	// Use useState to share state across components
	const themeMode = useState<ThemeMode>("theme-mode", () => "system");
	const resolvedTheme = useState<"light" | "dark">("resolved-theme", () => "light");

	// Cookie for persistence - readable by inline script before Vue loads
	const themeCookie = useCookie<ThemeMode | null>(THEME_COOKIE_KEY, {
		default: () => null,
		maxAge: 60 * 60 * 24 * 365, // 1 year
		sameSite: "lax",
	});

	// Nuxt UI keys its dark tokens off the `.dark` class managed by @nuxtjs/color-mode,
	// which is a separate authority from our `data-theme`. Mirror our mode into
	// color-mode's preference (identical system|light|dark vocabulary) so Nuxt UI
	// surfaces (changelog modal/popover/badges) flip with the site toggle. `data-theme`
	// stays the SCSS authority; color-mode is only the class authority. One control point.
	const colorMode = useColorMode();

	/**
	 * Get the system's preferred color scheme
	 */
	const getSystemTheme = (): "light" | "dark" => {
		if (!import.meta.client) return "light";
		return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
	};

	/**
	 * Apply the theme to the document root element
	 */
	const applyTheme = (theme: "light" | "dark") => {
		if (!import.meta.client) return;

		document.documentElement.setAttribute("data-theme", theme);
		resolvedTheme.value = theme;
	};

	/**
	 * Update the theme based on current mode
	 */
	const updateTheme = () => {
		if (themeMode.value === "system") {
			applyTheme(getSystemTheme());
		} else {
			applyTheme(themeMode.value);
		}
	};

	/**
	 * Set the theme mode and persist to cookie and localStorage
	 */
	const setTheme = (mode: ThemeMode) => {
		themeMode.value = mode;
		themeCookie.value = mode; // Store in cookie for FOUC prevention on next load
		colorMode.preference = mode; // keep Nuxt UI's .dark class in lockstep

		if (import.meta.client) {
			localStorage.setItem(THEME_STORAGE_KEY, mode); // Keep localStorage as backup
		}

		updateTheme();
	};

	/**
	 * Toggle between light and dark themes
	 * If currently on system, will switch to the opposite of the current resolved theme
	 */
	const toggleTheme = () => {
		if (themeMode.value === "system") {
			// If on system, switch to the opposite of current resolved theme
			setTheme(resolvedTheme.value === "dark" ? "light" : "dark");
		} else {
			setTheme(themeMode.value === "dark" ? "light" : "dark");
		}
	};

	/**
	 * Cycle through themes: system -> light -> dark -> system
	 */
	const cycleTheme = () => {
		const order: ThemeMode[] = ["system", "light", "dark"];
		const currentIndex = order.indexOf(themeMode.value);
		const nextIndex = (currentIndex + 1) % order.length;
		//@ts-expect-error
		setTheme(order[nextIndex]);
	};

	/**
	 * Reset to system preference
	 */
	const resetToSystem = () => {
		setTheme("system");
	};

	// Computed properties
	const isDark = computed(() => resolvedTheme.value === "dark");
	const isLight = computed(() => resolvedTheme.value === "light");
	const isSystemMode = computed(() => themeMode.value === "system");

	/**
	 * Remove initial inline styles that were added to prevent FOUC.
	 * Called after the app has mounted and CSS has taken over.
	 */
	const removeInitialStyles = () => {
		if (!import.meta.client) return;

		const initialStyleEl = document.getElementById("initial-theme-styles");
		if (initialStyleEl) {
			initialStyleEl.remove();
		}
	};

	// Initialize on server: mirror the cookie into shared state and color-mode's
	// preference so SSR-rendered output agrees with what the client will resolve
	// (system mode stays at the light default; only the client can resolve it).
	if (import.meta.server) {
		const savedTheme = themeCookie.value;
		if (savedTheme && ["light", "dark", "system"].includes(savedTheme)) {
			themeMode.value = savedTheme;
			if (savedTheme !== "system") {
				resolvedTheme.value = savedTheme;
			}
		}
		colorMode.preference = themeMode.value;
	}

	// Initialize on client
	if (import.meta.client) {
		// Load saved preference - prioritize cookie, fall back to localStorage
		const savedTheme = themeCookie.value || (localStorage.getItem(THEME_STORAGE_KEY) as ThemeMode | null);
		if (savedTheme && ["light", "dark", "system"].includes(savedTheme)) {
			themeMode.value = savedTheme;
			// Sync cookie if theme was only in localStorage (migration)
			if (!themeCookie.value) {
				themeCookie.value = savedTheme;
			}
		}

		// Sync color-mode to the resolved mode on load so Nuxt UI's .dark class matches
		// the stored theme without a toggle (closes a light-flash on an on-load popover).
		colorMode.preference = themeMode.value;

		// Apply initial theme
		updateTheme();

		// Listen for system theme changes (register only once)
		if (!mediaQueryListenerRegistered) {
			const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
			mediaQuery.addEventListener("change", () => {
				if (themeMode.value === "system") {
					updateTheme();
				}
			});
			mediaQueryListenerRegistered = true;
		}

		// Remove initial styles after app is mounted
		// Use nextTick to ensure Vue has finished rendering
		if (typeof onMounted !== "undefined") {
			onMounted(() => {
				nextTick(() => {
					removeInitialStyles();
				});
			});
		}
	}

	return {
		/** Current theme mode setting ('light', 'dark', or 'system') */
		themeMode: readonly(themeMode),
		/** The actual resolved theme being displayed ('light' or 'dark') */
		resolvedTheme: readonly(resolvedTheme),
		/** Whether the current resolved theme is dark */
		isDark,
		/** Whether the current resolved theme is light */
		isLight,
		/** Whether the theme mode is set to follow system preference */
		isSystemMode,
		/** Set the theme mode */
		setTheme,
		/** Toggle between light and dark */
		toggleTheme,
		/** Cycle through system -> light -> dark -> system */
		cycleTheme,
		/** Reset to system preference */
		resetToSystem,
	};
}
